import { useCallback, useEffect, useState } from 'react';
import { api, ApiError, type BooksType, type Pricing, type TaxationForm, type VatStatus, type ZusScheme,
  LEGAL_FORMS,
  type LegalForm,
} from '../api';
import type { Dictionary, Locale } from '../i18n';
import { localeNames, locales } from '../i18n';
import { Arrow, Check, Error as ErrorIcon, Info, Mark, Shield } from '../components/Icons';
import { capPercent, type Path } from './Start';
import { checkPassword, generatePassword, passwordAccepted, type PasswordRule } from '../password';

type Step = 'account' | 'code' | 'company' | 'settlements' | 'done';

/** Co trzeba pamiętać po powrocie z logowania — formularze wypełnia się dopiero potem. */
type Progress = { step: Step; path: Path; email: string; companyId?: string };

const PROGRESS_KEY = 'firmeto-rejestracja';

export function readProgress(): Progress | null {
  const raw = window.sessionStorage.getItem(PROGRESS_KEY);
  return raw ? (JSON.parse(raw) as Progress) : null;
}

function saveProgress(progress: Progress | null) {
  if (progress) window.sessionStorage.setItem(PROGRESS_KEY, JSON.stringify(progress));
  else window.sessionStorage.removeItem(PROGRESS_KEY);
}

/** Wybór z kroku 3 przekładamy na ustawienia podatkowe firmy. */
type Choice = 'RYCZALT' | 'KPIR' | 'FULL' | 'UNSURE';

/**
 * Rejestracja w trzech krokach (projekt „Start, logowanie, rejestracja”, ekrany 04–07).
 *
 * Krok pierwszy kończy się wpisaniem kodu z wiadomości, choć projekt pokazuje potwierdzanie
 * adresu dopiero na końcu. Powód jest twardy: firmy nie da się założyć, dopóki adres nie jest
 * potwierdzony — więc pytanie o kod na końcu zatrzymałoby człowieka na ostatnim przycisku.
 *
 * Między krokiem pierwszym a drugim przeglądarka wychodzi do Keycloaka po zalogowanie.
 * Dane firmy jeszcze wtedy nie są wpisane, więc nic nie przepada.
 */
export function Register({
  t,
  locale,
  path,
  signedIn,
  onLocale,
  onSignIn,
  onEnter,
  onBack,
}: {
  t: Dictionary;
  locale: Locale;
  path: Path;
  signedIn: boolean;
  onLocale: (locale: Locale) => void;
  onSignIn: (email: string) => void;
  onEnter: () => void;
  onBack: () => void;
}) {
  const resumed = readProgress();
  const [step, setStep] = useState<Step>(resumed?.step ?? 'account');
  const [email, setEmail] = useState(resumed?.email ?? '');
  const [companyId, setCompanyId] = useState(resumed?.companyId ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [info, setInfo] = useState<string | null>(null);
  const [pricing, setPricing] = useState<Pricing | null>(null);

  useEffect(() => {
    api.pricing().then(setPricing).catch(() => setPricing(null));
  }, []);

  const run = useCallback(async (action: () => Promise<void>) => {
    setBusy(true);
    setError(null);
    setInfo(null);
    try {
      await action();
    } catch (failure) {
      setError(failure instanceof ApiError ? failure.message : String(failure));
    } finally {
      setBusy(false);
    }
  }, []);

  const stepNumber = step === 'account' || step === 'code' ? 1 : step === 'company' ? 2 : 3;

  return (
    <div className={step === 'account' ? 'frame' : 'frame solo'}>
      {step === 'account' && <BrandSide t={t} />}

      <div className="pane">
        {step !== 'done' && (
          <div className="top">
            <Steps t={t} current={stepNumber} />
            {step === 'account' && (
              <div className="langsel" role="group" aria-label={t.language}>
                {locales.map((code) => (
                  <button key={code} type="button" aria-pressed={code === locale} onClick={() => onLocale(code)}>
                    {localeNames[code]}
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {step === 'account' && (
          <AccountStep
            t={t}
            locale={locale}
            busy={busy}
            onBack={onBack}
            onSubmit={(form) =>
              run(async () => {
                await api.register({
                  email: form.email,
                  password: form.password,
                  fullName: `${form.firstName} ${form.lastName}`.trim(),
                  uiLocale: locale,
                  acceptedTerms: true,
                  wantsLawUpdates: form.lawUpdates,
                });
                setEmail(form.email);
                saveProgress({ step: 'code', path, email: form.email });
                setStep('code');
                setInfo(t.register.codeSent);
              })
            }
          />
        )}

        {step === 'code' && (
          <CodeStep
            t={t}
            email={email}
            busy={busy}
            onResend={() => run(() => api.resendVerification(email, locale).then(() => setInfo(t.register.codeResent)))}
            onSubmit={(code) =>
              run(async () => {
                await api.verifyEmail(email, code);
                saveProgress({ step: 'company', path, email });
                // Firmę zakłada się już jako zalogowany, więc tu wychodzimy do Keycloaka
                onSignIn(email);
              })
            }
          />
        )}

        {step === 'company' && (
          <CompanyStep
            t={t}
            busy={busy}
            signedIn={signedIn}
            onSignIn={() => onSignIn(email)}
            onSubmit={(company) =>
              run(async () => {
                const created = await api.createCompany(company);
                setCompanyId(created.id);
                saveProgress({ step: 'settlements', path, email, companyId: created.id });
                setStep('settlements');
              })
            }
          />
        )}

        {step === 'settlements' && (
          <SettlementsStep
            t={t}
            busy={busy}
            pricing={pricing}
            onSubmit={(settings) =>
              run(async () => {
                if (settings) await api.addSettings(companyId, settings);
                saveProgress({ step: 'done', path, email, companyId });
                setStep('done');
              })
            }
          />
        )}

        {step === 'done' && (
          <DoneStep
            t={t}
            onEnter={() => {
              saveProgress(null);
              onEnter();
            }}
          />
        )}

        {busy && <p className="muted">{t.loading}</p>}
        {error && (
          <div className="alert a-bad">
            <ErrorIcon />
            <div>{error}</div>
          </div>
        )}
        {info && !error && (
          <div className="alert a-info">
            <Info />
            <div>{info}</div>
          </div>
        )}
      </div>
    </div>
  );
}

function BrandSide({ t }: { t: Dictionary }) {
  return (
    <div className="side">
      <div className="brand">
        <Mark white />
        <b>{t.appName}</b>
      </div>
      <div className="claim">{t.register.claim}</div>
      <ul>
        {t.register.stepNames.map((name) => (
          <li key={name}>
            <Check />
            {name}
          </li>
        ))}
      </ul>
      <div className="side-foot">
        <span>
          <Shield className="inline-icon" /> {t.register.euData}
        </span>
      </div>
    </div>
  );
}

function Steps({ t, current }: { t: Dictionary; current: number }) {
  return (
    <div className="steps" style={{ flex: 1 }}>
      {t.register.stepNames.map((name, index) => {
        const number = index + 1;
        const state = number < current ? 'done' : number === current ? 'on' : '';
        return (
          <span key={name} style={{ display: 'contents' }}>
            {index > 0 && <span className="ln" />}
            <span className={`st ${state}`}>
              <i>{number < current ? <Check /> : number}</i>
              {name}
            </span>
          </span>
        );
      })}
    </div>
  );
}

type AccountForm = {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  repeated: string;
  lawUpdates: boolean;
};

function AccountStep({
  t,
  locale,
  busy,
  onSubmit,
  onBack,
}: {
  t: Dictionary;
  locale: Locale;
  busy: boolean;
  onSubmit: (form: AccountForm) => void;
  onBack: () => void;
}) {
  const [form, setForm] = useState<AccountForm>({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    repeated: '',
    lawUpdates: false,
  });
  const [terms, setTerms] = useState(false);
  // Hasło pokazujemy na życzenie i po wygenerowaniu — inaczej nie da się go przepisać
  const [visible, setVisible] = useState(false);
  const [generated, setGenerated] = useState(false);
  // O niezgodności mówimy dopiero, gdy powtórzenie w ogóle zaczęto pisać — inaczej
  // ostrzeżenie miga przy pierwszym znaku i nic nie wnosi
  const mismatch = form.repeated !== '' && form.repeated !== form.password;
  // Zasady sprawdzamy przy każdym znaku, ale pokazujemy je dopiero, gdy jest co sprawdzać
  const rules = checkPassword(form.password, form.email);
  const complete =
    form.firstName.trim() !== '' &&
    form.lastName.trim() !== '' &&
    form.email.trim() !== '' &&
    passwordAccepted(form.password, form.email) &&
    form.repeated === form.password &&
    terms;

  /**
   * Hasło z generatora trafia od razu w oba pola i do schowka.
   *
   * Kazanie przepisywania go ręcznie do drugiego pola kończyłoby się literówką — a to
   * właśnie przed nią miało bronić powtórzenie. Pokazujemy je jawnie, bo hasła, którego
   * nie widać, nikt nie zapisze.
   */
  const useGenerated = () => {
    const password = generatePassword();
    setForm((current) => ({ ...current, password, repeated: password }));
    setVisible(true);
    setGenerated(true);
    void navigator.clipboard?.writeText(password).catch(() => undefined);
  };

  return (
    <>
      <div className="hd">
        <h3>{t.register.account.title}</h3>
        <p>{t.register.account.lead}</p>
      </div>
      <form
        className="entry-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(form);
        }}
      >
        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="firstName">{t.register.account.firstName}</label>
            <input
              id="firstName"
              className="inp"
              autoComplete="given-name"
              value={form.firstName}
              onChange={(event) => setForm({ ...form, firstName: event.target.value })}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="lastName">{t.register.account.lastName}</label>
            <input
              id="lastName"
              className="inp"
              autoComplete="family-name"
              value={form.lastName}
              onChange={(event) => setForm({ ...form, lastName: event.target.value })}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="email">{t.register.account.email}</label>
          <input
            id="email"
            className="inp"
            type="email"
            autoComplete="username"
            value={form.email}
            onChange={(event) => setForm({ ...form, email: event.target.value })}
          />
          <span className="hint">{t.register.account.emailHint}</span>
        </div>

        <div className="field">
          <div className="field-head">
            <label htmlFor="password">{t.register.account.password}</label>
            <div className="field-tools">
              {form.password !== '' && (
                <button type="button" className="btn plain tiny" onClick={() => setVisible(!visible)}>
                  {visible ? t.register.account.hidePassword : t.register.account.showPassword}
                </button>
              )}
              <button type="button" className="btn plain tiny" onClick={useGenerated}>
                {t.register.account.generate}
              </button>
            </div>
          </div>
          <input
            id="password"
            className="inp"
            type={visible ? 'text' : 'password'}
            autoComplete="new-password"
            value={form.password}
            onChange={(event) => {
              setForm({ ...form, password: event.target.value });
              setGenerated(false);
            }}
          />
          {form.password === '' ? (
            <span className="hint">{t.register.account.passwordHint}</span>
          ) : (
            <ul className="rules">
              {(['length', 'notEmail', 'maxLength'] as PasswordRule[])
                // Górna granica to rzadki przypadek — mówimy o niej dopiero, gdy zostanie złamana
                .filter((rule) => rule !== 'maxLength' || !rules.maxLength)
                .map((rule) => (
                  <li key={rule} className={rules[rule] ? 'ok' : 'todo'}>
                    {rules[rule] ? <Check /> : <span className="dot" />}
                    {t.register.account.passwordRules[rule]}
                  </li>
                ))}
            </ul>
          )}
          {generated && <span className="hint ok">{t.register.account.generated}</span>}
        </div>

        <div className="field">
          <label htmlFor="repeated">{t.register.account.repeatPassword}</label>
          <input
            id="repeated"
            className={`inp ${mismatch ? 'err' : ''}`}
            type={visible ? 'text' : 'password'}
            autoComplete="new-password"
            value={form.repeated}
            onChange={(event) => setForm({ ...form, repeated: event.target.value })}
          />
          {mismatch && <span className="hint err">{t.register.account.passwordsDiffer}</span>}
        </div>

        <div className="field">
          <label htmlFor="uiLocale">{t.register.account.uiLocale}</label>
          <select id="uiLocale" className="inp" value={locale} disabled>
            <option value={locale}>{localeNames[locale]}</option>
          </select>
          <span className="hint">{t.register.account.uiLocaleHint}</span>
        </div>

        <label className="check">
          <input type="checkbox" checked={terms} onChange={(event) => setTerms(event.target.checked)} />
          <i>
            <Check />
          </i>
          <span>{t.register.account.terms}</span>
        </label>

        <label className="check">
          <input
            type="checkbox"
            checked={form.lawUpdates}
            onChange={(event) => setForm({ ...form, lawUpdates: event.target.checked })}
          />
          <i>
            <Check />
          </i>
          <span>{t.register.account.lawUpdates}</span>
        </label>

        <button type="submit" className="btn wide" disabled={!complete || busy}>
          {t.register.account.next}
          <Arrow />
        </button>
      </form>
      <div className="entry-foot">
        <button type="button" className="btn plain" onClick={onBack}>
          {t.register.backToStart}
        </button>
      </div>
    </>
  );
}

function CodeStep({
  t,
  email,
  busy,
  onSubmit,
  onResend,
}: {
  t: Dictionary;
  email: string;
  busy: boolean;
  onSubmit: (code: string) => void;
  onResend: () => void;
}) {
  const [code, setCode] = useState('');
  return (
    <>
      <div className="hd">
        <h3>{t.register.code.title}</h3>
        <p>{t.register.code.lead(email)}</p>
      </div>
      <form
        className="entry-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit(code);
        }}
      >
        <div className="field">
          <label htmlFor="code">{t.register.code.label}</label>
          <input
            id="code"
            className="inp mono"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
          />
          <span className="hint">{t.register.code.hint}</span>
        </div>
        <button type="submit" className="btn wide" disabled={code.length !== 6 || busy}>
          {t.register.code.confirm}
        </button>
        <button type="button" className="btn plain wide" onClick={onResend} disabled={busy}>
          {t.register.code.resend}
        </button>
      </form>
    </>
  );
}

type CompanyForm = {
  nip: string;
  legalForm: LegalForm;
  name: string;
  addressLine: string;
  postalCode: string;
  city: string;
  businessStartedOn: string;
};

function CompanyStep({
  t,
  busy,
  signedIn,
  onSignIn,
  onSubmit,
}: {
  t: Dictionary;
  busy: boolean;
  signedIn: boolean;
  onSignIn: () => void;
  onSubmit: (company: CompanyForm) => void;
}) {
  const [form, setForm] = useState<CompanyForm>({
    nip: '',
    legalForm: 'JDG',
    name: '',
    addressLine: '',
    postalCode: '',
    city: '',
    businessStartedOn: '',
  });
  const digits = form.nip.replace(/\D/g, '');
  const nipReady = digits.length === 10;
  const complete = nipReady && form.name.trim() !== '';

  if (!signedIn) {
    return (
      <>
        <div className="hd">
          <h3>{t.register.company.title}</h3>
          <p>{t.register.signInToContinue}</p>
        </div>
        <button type="button" className="btn" onClick={onSignIn}>
          {t.register.signIn}
        </button>
      </>
    );
  }

  return (
    <>
      <div className="hd">
        <h3>{t.register.company.title}</h3>
        <p>{t.register.company.lead}</p>
      </div>
      <form
        className="entry-form"
        onSubmit={(event) => {
          event.preventDefault();
          onSubmit({ ...form, nip: digits });
        }}
      >
        <div className="field">
          <label htmlFor="nip">{t.register.company.nip}</label>
          <input
            id="nip"
            className={`inp mono ${form.nip === '' ? '' : nipReady ? 'ok' : 'err'}`}
            inputMode="numeric"
            value={form.nip}
            onChange={(event) => setForm({ ...form, nip: event.target.value })}
          />
          {form.nip !== '' && !nipReady && (
            <span className="hint err">
              <ErrorIcon />
              {t.register.company.nipLength(digits.length)}
            </span>
          )}
        </div>

        {/* Podpowiedzi z GUS jeszcze nie ma — mówimy to wprost, zamiast udawać, że pole
            wypełni się samo */}
        <div className="alert a-info">
          <Info />
          <div>
            <b>{t.register.company.noRegistryTitle}</b>
            <p>{t.register.company.noRegistryLead}</p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="legalForm">{t.register.company.legalForm}</label>
          <select
            id="legalForm"
            className="inp"
            value={form.legalForm}
            onChange={(event) => setForm({ ...form, legalForm: event.target.value as CompanyForm['legalForm'] })}
          >
            {LEGAL_FORMS.map((code) => (
              <option key={code} value={code}>
                {t.companies.forms[code]}
              </option>
            ))}
          </select>
        </div>

        <div className="field">
          <label htmlFor="name">{t.register.company.name}</label>
          <input
            id="name"
            className="inp"
            value={form.name}
            onChange={(event) => setForm({ ...form, name: event.target.value })}
          />
          <span className="hint">{t.register.company.nameHint}</span>
        </div>

        <div className="field">
          <label htmlFor="addressLine">{t.register.company.address}</label>
          <input
            id="addressLine"
            className="inp"
            value={form.addressLine}
            onChange={(event) => setForm({ ...form, addressLine: event.target.value })}
          />
        </div>

        <div className="row" style={{ flexWrap: 'nowrap' }}>
          <div className="field" style={{ flex: '0 0 8rem' }}>
            <label htmlFor="postalCode">{t.register.company.postalCode}</label>
            <input
              id="postalCode"
              className="inp mono"
              value={form.postalCode}
              onChange={(event) => setForm({ ...form, postalCode: event.target.value })}
            />
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="city">{t.register.company.city}</label>
            <input
              id="city"
              className="inp"
              value={form.city}
              onChange={(event) => setForm({ ...form, city: event.target.value })}
            />
          </div>
        </div>

        <div className="field">
          <label htmlFor="businessStartedOn">{t.register.company.startedOn}</label>
          <input
            id="businessStartedOn"
            className="inp"
            type="date"
            value={form.businessStartedOn}
            onChange={(event) => setForm({ ...form, businessStartedOn: event.target.value })}
          />
          <span className="hint">{t.register.company.startedOnHint}</span>
        </div>

        <button type="submit" className="btn wide" disabled={!complete || busy}>
          {t.register.company.next}
          <Arrow />
        </button>
      </form>
    </>
  );
}

type NewSettingsForm = {
  validFrom: string;
  taxationForm: TaxationForm;
  booksType: BooksType;
  vatStatus: VatStatus;
  vatExemptionBasis: string | null;
  zusScheme: ZusScheme | null;
  cashMethod: boolean;
  smallTaxpayer: boolean;
  sicknessInsurance: boolean;
  /** Okres zaliczek na podatek dochodowy; przy rejestracji zostaje miesięczny — zmienia się w ustawieniach */
  incomeTaxPeriod: 'MONTHLY' | 'QUARTERLY';
};

function SettlementsStep({
  t,
  busy,
  pricing,
  onSubmit,
}: {
  t: Dictionary;
  busy: boolean;
  pricing: Pricing | null;
  onSubmit: (settings: NewSettingsForm | null) => void;
}) {
  const [choice, setChoice] = useState<Choice>('KPIR');
  const [incomeTax, setIncomeTax] = useState<'SKALA' | 'LINIOWY' | 'CIT'>('SKALA');
  const [vat, setVat] = useState<'ACTIVE' | 'EXEMPT_LIMIT' | 'EXEMPT_KIND'>('ACTIVE');
  const [quarterly, setQuarterly] = useState(false);
  const [zus, setZus] = useState<ZusScheme>('PELNY');
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [rules, setRules] = useState(false);

  const price = (plan: 'RYCZALT' | 'KPIR' | 'FULL') =>
    pricing?.plans.find((entry) => entry.plan === plan)?.net;

  const cards: { id: Choice; plan?: 'RYCZALT' | 'KPIR' | 'FULL' }[] = [
    { id: 'RYCZALT', plan: 'RYCZALT' },
    { id: 'KPIR', plan: 'KPIR' },
    { id: 'FULL', plan: 'FULL' },
    { id: 'UNSURE' },
  ];

  function submit() {
    if (choice === 'UNSURE') {
      onSubmit(null);
      return;
    }
    onSubmit({
      validFrom: `${month}-01`,
      taxationForm: choice === 'RYCZALT' ? 'RYCZALT' : incomeTax,
      booksType: choice,
      vatStatus: vat === 'ACTIVE' ? (quarterly ? 'ACTIVE_QUARTERLY' : 'ACTIVE_MONTHLY') : 'EXEMPT',
      vatExemptionBasis:
        vat === 'EXEMPT_LIMIT'
          ? 'art. 113 ust. 1 ustawy o VAT'
          : vat === 'EXEMPT_KIND'
            ? 'art. 43 ust. 1 ustawy o VAT'
            : null,
      zusScheme: zus,
      cashMethod: false,
      smallTaxpayer: false,
      sicknessInsurance: true,
      // Przy rejestracji pytamy o okres VAT-u; zaliczki zostają miesięczne, a kwartalne
      // wybiera się w ustawieniach razem z deklaracją „mały podatnik"
      incomeTaxPeriod: 'MONTHLY',
    });
  }

  return (
    <>
      <div className="hd">
        <h3>{t.register.settlements.title}</h3>
        <p>{t.register.settlements.lead}</p>
      </div>

      <div className="forms">
        {cards.map(({ id, plan }) => (
          <button
            key={id}
            type="button"
            className="fcard"
            aria-pressed={choice === id}
            onClick={() => setChoice(id)}
          >
            <b>{t.register.settlements.plans[id].title}</b>
            <span className="p">
              {plan ? t.register.settlements.perMonth(price(plan)) : t.register.settlements.unsurePrice}
            </span>
            <span>{t.register.settlements.plans[id].lead}</span>
          </button>
        ))}
      </div>

      {choice !== 'UNSURE' && (
        <form
          className="entry-form wide"
          onSubmit={(event) => {
            event.preventDefault();
            submit();
          }}
        >
          {choice !== 'RYCZALT' && (
            <div className="field">
              <label htmlFor="incomeTax">{t.register.settlements.incomeTax}</label>
              <select
                id="incomeTax"
                className="inp"
                value={incomeTax}
                onChange={(event) => setIncomeTax(event.target.value as typeof incomeTax)}
              >
                <option value="SKALA">{t.register.settlements.skala}</option>
                <option value="LINIOWY">{t.register.settlements.liniowy}</option>
                <option value="CIT">{t.register.settlements.cit}</option>
              </select>
            </div>
          )}

          <div className="field">
            <label htmlFor="vat">{t.register.settlements.vat}</label>
            <select id="vat" className="inp" value={vat} onChange={(event) => setVat(event.target.value as typeof vat)}>
              <option value="ACTIVE">{t.register.settlements.vatActive}</option>
              <option value="EXEMPT_LIMIT">{t.register.settlements.vatExemptLimit}</option>
              <option value="EXEMPT_KIND">{t.register.settlements.vatExemptKind}</option>
            </select>
          </div>

          {vat === 'ACTIVE' && (
            <div className="field">
              <label htmlFor="vatPeriod">{t.register.settlements.vatPeriod}</label>
              <select
                id="vatPeriod"
                className="inp"
                value={quarterly ? 'Q' : 'M'}
                onChange={(event) => setQuarterly(event.target.value === 'Q')}
              >
                <option value="M">{t.register.settlements.monthly}</option>
                <option value="Q">{t.register.settlements.quarterly}</option>
              </select>
            </div>
          )}

          {/* ZUS-u projekt nie pokazuje, a bez niego pierwsze rozliczenie byłoby nieprawdziwe:
              składki to zwykle większa kwota niż zaliczka na podatek */}
          <div className="field">
            <label htmlFor="zus">{t.register.settlements.zus}</label>
            <select
              id="zus"
              className="inp"
              value={zus}
              onChange={(event) => setZus(event.target.value as ZusScheme)}
            >
              <option value="ULGA_NA_START">{t.register.settlements.zusSchemes.ULGA_NA_START}</option>
              <option value="PREFERENCYJNY">{t.register.settlements.zusSchemes.PREFERENCYJNY}</option>
              <option value="MALY_ZUS_PLUS">{t.register.settlements.zusSchemes.MALY_ZUS_PLUS}</option>
              <option value="PELNY">{t.register.settlements.zusSchemes.PELNY}</option>
              <option value="BRAK">{t.register.settlements.zusSchemes.BRAK}</option>
            </select>
          </div>

          <div className="field">
            <label htmlFor="validFrom">{t.register.settlements.from}</label>
            <input
              id="validFrom"
              className="inp"
              type="month"
              value={month}
              onChange={(event) => setMonth(event.target.value)}
            />
            <span className="hint">{t.register.settlements.fromHint}</span>
          </div>

          <label className="check">
            <input type="checkbox" checked={rules} onChange={(event) => setRules(event.target.checked)} />
            <i>
              <Check />
            </i>
            <span>{t.register.settlements.feeRule(pricing?.trialMonths ?? 3, capPercent(pricing))}</span>
          </label>

          <button type="submit" className="btn accent wide" disabled={!rules || busy}>
            {t.register.settlements.finish}
            <Arrow />
          </button>
        </form>
      )}

      {choice === 'UNSURE' && (
        <div className="entry-form">
          <div className="alert a-info">
            <Info />
            <div>
              <b>{t.register.settlements.unsureTitle}</b>
              <p>{t.register.settlements.unsureLead}</p>
            </div>
          </div>
          <button type="button" className="btn accent wide" onClick={submit} disabled={busy}>
            {t.register.settlements.finish}
            <Arrow />
          </button>
        </div>
      )}
    </>
  );
}

function DoneStep({ t, onEnter }: { t: Dictionary; onEnter: () => void }) {
  return (
    <div className="done">
      <div className="mark">
        <Check />
      </div>
      <div>
        <h3>{t.register.done.title}</h3>
        <p>{t.register.done.lead}</p>
      </div>
      <div className="next">
        {t.register.done.next.map((item, index) => (
          <div className="it" key={item.title}>
            <i>{index + 1}</i>
            <div className="tx">
              <b>{item.title}</b>
              <span>{item.lead}</span>
            </div>
          </div>
        ))}
      </div>
      <button type="button" className="btn wide" onClick={onEnter}>
        {t.register.done.enter}
      </button>
    </div>
  );
}
